import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";

/**
 * React hook that fires exactly one GA4 `page_view` event per
 * pathname change, session-wide.
 *
 * Mount this inside <BrowserRouter> at the top of the app. See
 * src/App.js AppRouter() for the single call site.
 *
 * DESIGN NOTE — module-level guard, not useRef
 * -------------------------------------------
 * React 18 StrictMode in development intentionally unmounts and
 * remounts every component once, right after the initial mount, to
 * catch effects that don't handle cleanup correctly. That
 * unmount+remount DESTROYS useRef state. If the guard for "did I
 * already fire a page_view for this pathname?" lived in a useRef,
 * the initial `/` mount would fire once, StrictMode would unmount
 * (ref destroyed), remount, and fire again — producing two
 * page_views for the initial load. Verified empirically in preview
 * before switching to module-level state.
 *
 * Module-level state persists across the StrictMode simulated
 * remount because the module is only imported once. This is the
 * only correct place for the "have we already tracked this
 * pathname?" flag.
 *
 * PAGE TITLE TIMING
 * -----------------
 * react-helmet-async writes to document.title after the component
 * tree renders. We defer the page_view to a microtask
 * (`Promise.resolve().then(...)`) so the fresh title is captured.
 * We do not defer longer than that — a missed page_view from a
 * fast route change is worse than a slightly stale title.
 */

// Module-level guard: survives React 18 StrictMode remounts because
// modules are singletons. Reset only on full page reload — which is
// exactly when we DO want the initial page_view to fire again.
let lastTrackedPathname = null;

export function useGAPageViewTracking() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname || "/";
    if (lastTrackedPathname === pathname) return;
    lastTrackedPathname = pathname;

    // Defer one microtask so react-helmet-async has a chance to
    // update <title> before we read document.title.
    Promise.resolve().then(() => {
      trackPageView(pathname);
    });
  }, [location.pathname]);
}

// Testing hook: reset the module-level guard between test cases so
// this hook can be exercised in unit tests without accumulated state.
// NOT called anywhere in production code.
export function __resetGATrackingForTests() {
  lastTrackedPathname = null;
}
